import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  Globe,
  HelpCircle,
  Users,
  Clock,
  Mail,
  User,
  MessageCircle,
  Smile,
  MessageSquare,
  Phone,
  Smartphone,
  Settings,
  Check,
  Rocket,
  Plus,
  Trash2,
  ShieldCheck,
  Pencil,
  Home,
  Sparkles,
  Loader2,
  Building2,
  Info,
  Briefcase,
  BookOpen,
} from "lucide-react";
import Sidebar from "@/components/Sidebar";
import Cookies from "js-cookie";
import { getClientAuthToken } from "@/lib/apiAuth";
import RoofingBotSettings from "./RoofingBotSettings";

// Local Storage Key
const STORAGE_KEY = "ai_broker_onboarding_data_v2";

// Initial form state matching the JSON structure
const initialFormData = {
  knowledge_base: {
    agency_name: "",
    website_url: "",
    handoff_settings: {
      lead_agent_name: "",
      lead_agent_role: "",
      lead_agent_phone: "",
      lead_agent_email: "",
      allowed_channels: [],
    },
    vendor_faqs: [
      {
        topic: "Marketing Strategy",
        details: "We use a premium omnichannel marketing strategy. This includes professional photography, drone footage, 3D virtual tours, and premium placement on realestate.com.au and Domain. We also run targeted social media campaigns on Facebook and Instagram to find passive buyers.",
      },
      {
        topic: "Sales Process & Methods",
        details: "We specialize in both Auctions and Private Treaty sales. The best method depends on the property and current market conditions. Our Senior Broker will recommend the exact strategy during the in-person appraisal to guarantee the highest price.",
      },
      {
        topic: "Agency Track Record",
        details: "Our agency consistently achieves results above the market average. We sell properties on average in 21 days with a 98% auction clearance rate. We have a database of over 5,000 active buyers ready to purchase in this area.",
      },
      {
        topic: "Market Conditions",
        details: "Currently, we are seeing a strong seller's market with high demand and low inventory. Quality homes are receiving multiple offers. It is an excellent time to list a property to maximize returns.",
      },
      {
        topic: "Preparing Home for Sale",
        details: "We provide full guidance on preparing your home. We recommend decluttering, minor touch-ups, and professional styling/staging, which can add 5–10% to the final sale price. We have a team of trusted tradespeople to help you.",
      },
    ],
  },
  qualification_logic: {
    buyer_questions: [
      { question: "1. Budget?", approved_reply: "" },
      { question: "2. Owner-occupier or Investor?", approved_reply: "" },
      { question: "3. Finance pre-approved?", approved_reply: "" },
    ],
    vendor_questions: [
      { question: "1. Property address", approved_reply: "Any" },
      { question: "2. Details: House or apartment? How many bedrooms?", approved_reply: "Any" },
      { question: "3. Timeline: Are you looking to sell urgently, or just exploring options for the future?", approved_reply: "Any" },
    ],
    tenant_questions: [],
    unqualified_action: "manager",
    strict_qualification_gate: false,
  },
  scheduling: {
    inspection_hours: "",
  },
  tone_of_voice: {
    bot_name: "",
    style: "",
    use_emojis: false,
    use_aussie_slang: false,
  },
};

// Email validation helper
const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// URL validation helper
const validateUrl = (url) => {
  if (!url) return false;
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// Progress Bar Component
const ProgressBar = ({ currentStep, totalSteps }) => {
  const progress = (currentStep / totalSteps) * 100;
  
  return (
    <div className="w-full" data-testid="progress-bar">
      <div className="flex justify-between items-center mb-2">
        <span className="font-mono text-[#00D1FF] text-sm">
          Step {currentStep} of {totalSteps}
        </span>
        <span className="font-mono text-[#9CA3AF] text-sm">
          {Math.round(progress)}% Complete
        </span>
      </div>
      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-[#00D1FF] to-[#00D1FF]/70 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

// Step Indicator Component
const StepIndicator = ({ steps, currentStep }) => {
  return (
    <div className="hidden lg:flex items-center justify-center gap-2 mb-8" data-testid="step-indicator">
      {steps.map((step, index) => {
        const stepNum = index + 1;
        const isActive = stepNum === currentStep;
        const isCompleted = stepNum < currentStep;
        
        return (
          <React.Fragment key={stepNum}>
            <div className="flex items-center gap-2">
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center font-mono text-xs transition-all duration-300 ${
                  isActive 
                    ? "bg-[#00D1FF] text-[#0F1115]" 
                    : isCompleted 
                    ? "bg-[#00D1FF]/20 text-[#00D1FF] border border-[#00D1FF]/50" 
                    : "bg-white/5 text-[#9CA3AF] border border-white/10"
                }`}
              >
                {isCompleted ? <Check className="w-4 h-4" /> : stepNum}
              </div>
              <span className={`text-xs font-body hidden xl:block ${
                isActive ? "text-[#F3F4F6]" : "text-[#9CA3AF]"
              }`}>
                {step.shortTitle}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div className={`w-8 h-0.5 ${
                isCompleted ? "bg-[#00D1FF]/50" : "bg-white/10"
              }`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// Form Input Component
const FormInput = ({ label, required, error, icon: Icon, ...props }) => (
  <div className="space-y-2">
    <label className="flex items-center gap-2 text-[#F3F4F6] text-sm font-body">
      {Icon && <Icon className="w-4 h-4 text-[#00D1FF]" />}
      {label}
      {required && <span className="text-[#00D1FF]">*</span>}
    </label>
    <input
      {...props}
      className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none transition-all font-body ${
        error
          ? "border-red-500/70 focus:border-red-500 focus:ring-1 focus:ring-red-500/50"
          : "border-white/10 focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50"
      }`}
    />
    {error && <p className="text-red-400 text-xs font-body">{error}</p>}
  </div>
);

// Form Input with Tooltip Component
const FormInputWithTooltip = ({ label, sublabel, tooltip, required, error, icon: Icon, ...props }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <label className="flex items-center gap-2 text-[#F3F4F6] text-sm font-body">
            {Icon && <Icon className="w-4 h-4 text-[#00D1FF]" />}
            {label}
            {required && <span className="text-[#00D1FF]">*</span>}
          </label>
          {sublabel && (
            <p className="text-[#9CA3AF] text-xs font-body mt-1 ml-6">{sublabel}</p>
          )}
        </div>
        {tooltip && (
          <div className="relative">
            <button
              type="button"
              className="p-1 text-[#9CA3AF] hover:text-[#00D1FF] transition-colors"
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              onClick={() => setShowTooltip(!showTooltip)}
              aria-label="More information"
            >
              <Info className="w-4 h-4" />
            </button>
            {showTooltip && (
              <div className="absolute right-0 top-8 z-50 w-72 p-3 rounded-lg bg-[#1a1d24] border border-white/20 shadow-xl">
                <p className="text-[#F3F4F6] text-xs font-body leading-relaxed">{tooltip}</p>
                <div className="absolute -top-2 right-2 w-3 h-3 bg-[#1a1d24] border-l border-t border-white/20 transform rotate-45" />
              </div>
            )}
          </div>
        )}
      </div>
      <input
        {...props}
        className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none transition-all font-body ${
          error
            ? "border-red-500/70 focus:border-red-500 focus:ring-1 focus:ring-red-500/50"
            : "border-white/10 focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50"
        }`}
      />
      {error && <p className="text-red-400 text-xs font-body">{error}</p>}
    </div>
  );
};

// Radio Group Component
const RadioGroup = ({ label, required, options, value, onChange, error, icon: Icon }) => (
  <div className="space-y-3">
    <label className="flex items-center gap-2 text-[#F3F4F6] text-sm font-body">
      {Icon && <Icon className="w-4 h-4 text-[#00D1FF]" />}
      {label}
      {required && <span className="text-[#00D1FF]">*</span>}
    </label>
    <div className="space-y-2">
      {options.map((option) => (
        <label
          key={option.value}
          className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
            value === option.value
              ? "bg-[#00D1FF]/10 border-[#00D1FF]/50"
              : "bg-white/5 border-white/10 hover:border-white/20"
          }`}
        >
          <input
            type="radio"
            name={label}
            value={option.value}
            checked={value === option.value}
            onChange={(e) => onChange(e.target.value)}
            className="sr-only"
          />
          <div
            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
              value === option.value
                ? "border-[#00D1FF] bg-[#00D1FF]"
                : "border-white/30"
            }`}
          >
            {value === option.value && (
              <div className="w-2 h-2 rounded-full bg-[#0F1115]" />
            )}
          </div>
          <div className="flex flex-col">
            <span className="text-[#F3F4F6] text-sm font-body">{option.label}</span>
            {option.description && (
              <span className="text-[#9CA3AF] text-xs font-body mt-1">{option.description}</span>
            )}
          </div>
        </label>
      ))}
    </div>
    {error && <p className="text-red-400 text-xs font-body">{error}</p>}
  </div>
);

// Toggle Switch Component
const ToggleSwitch = ({ label, checked, onChange, icon: Icon }) => (
  <label className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10 cursor-pointer hover:border-white/20 transition-all">
    <div className="flex items-center gap-3">
      {Icon && <Icon className="w-5 h-5 text-[#00D1FF]" />}
      <span className="text-[#F3F4F6] text-sm font-body">{label}</span>
    </div>
    <div
      className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${
        checked ? "bg-[#00D1FF]" : "bg-white/20"
      }`}
      onClick={() => onChange(!checked)}
    >
      <div
        className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform duration-300 ${
          checked ? "translate-x-7" : "translate-x-1"
        }`}
      />
    </div>
  </label>
);

// Select Component
const FormSelect = ({ label, required, options, value, onChange, error, icon: Icon }) => (
  <div className="space-y-2">
    <label className="flex items-center gap-2 text-[#F3F4F6] text-sm font-body">
      {Icon && <Icon className="w-4 h-4 text-[#00D1FF]" />}
      {label}
      {required && <span className="text-[#00D1FF]">*</span>}
    </label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-[#F3F4F6] focus:outline-none transition-all font-body appearance-none cursor-pointer ${
        error
          ? "border-red-500/70 focus:border-red-500 focus:ring-1 focus:ring-red-500/50"
          : "border-white/10 focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50"
      }`}
      style={{
        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%239CA3AF' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
        backgroundPosition: "right 0.75rem center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "1.5em 1.5em",
      }}
    >
      <option value="" className="bg-[#0F1115]">Select an option</option>
      {options.map((option) => (
        <option key={option.value} value={option.value} className="bg-[#0F1115]">
          {option.label}
        </option>
      ))}
    </select>
    {error && <p className="text-red-400 text-xs font-body">{error}</p>}
  </div>
);

// Dynamic Question Input Component
const QuestionInput = ({ index, question, approvedReply, onQuestionChange, onReplyChange, onRemove, canRemove }) => (
  <div className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-3">
    <div className="flex items-center justify-between">
      <span className="font-mono text-[#00D1FF] text-xs">Question {index + 1}</span>
      {canRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="text-[#9CA3AF] hover:text-red-400 transition-colors p-1"
          aria-label="Remove question"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
    <input
      type="text"
      value={question}
      onChange={(e) => onQuestionChange(e.target.value)}
      placeholder="Enter your question..."
      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50 transition-all font-body text-sm"
    />
    <input
      type="text"
      value={approvedReply}
      onChange={(e) => onReplyChange(e.target.value)}
      placeholder="Approved reply criteria (e.g., 'yes', 'more than $1M')"
      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50 transition-all font-body text-sm"
    />
  </div>
);

// Questions Section Component
const QuestionsSection = ({ label, required, questions, onChange, icon: Icon, error }) => {
  const addQuestion = () => {
    onChange([...questions, { question: "", approved_reply: "" }]);
  };

  const removeQuestion = (index) => {
    onChange(questions.filter((_, i) => i !== index));
  };

  const updateQuestion = (index, field, value) => {
    const updated = questions.map((q, i) => 
      i === index ? { ...q, [field]: value } : q
    );
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 text-[#F3F4F6] text-sm font-body">
        {Icon && <Icon className="w-4 h-4 text-[#00D1FF]" />}
        {label}
        {required && <span className="text-[#00D1FF]">*</span>}
      </label>
      
      <div className="space-y-3">
        {questions.map((q, index) => (
          <QuestionInput
            key={index}
            index={index}
            question={q.question}
            approvedReply={q.approved_reply}
            onQuestionChange={(value) => updateQuestion(index, "question", value)}
            onReplyChange={(value) => updateQuestion(index, "approved_reply", value)}
            onRemove={() => removeQuestion(index)}
            canRemove={questions.length > 1 || !required}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={addQuestion}
        className="flex items-center gap-2 text-[#00D1FF] text-sm font-body hover:text-[#00D1FF]/80 transition-colors py-2"
      >
        <Plus className="w-4 h-4" />
        Add Question
      </button>
      
      {error && <p className="text-red-400 text-xs font-body">{error}</p>}
    </div>
  );
};

// Dynamic FAQ (topic/details) Input Component
const FaqInput = ({ index, topic, details, onTopicChange, onDetailsChange, onRemove, canRemove }) => (
  <div className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-3">
    <div className="flex items-center justify-between">
      <span className="font-mono text-[#00D1FF] text-xs">FAQ {index + 1}</span>
      {canRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="text-[#9CA3AF] hover:text-red-400 transition-colors p-1"
          aria-label="Remove FAQ"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
    <input
      type="text"
      value={topic}
      onChange={(e) => onTopicChange(e.target.value)}
      placeholder="Topic (e.g., 'Marketing Strategy')"
      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50 transition-all font-body text-sm"
    />
    <textarea
      value={details}
      onChange={(e) => onDetailsChange(e.target.value)}
      placeholder="Answer / details the bot will use to respond..."
      rows={4}
      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50 transition-all font-body text-sm resize-y"
    />
  </div>
);

// FAQs Section Component (topic/details format)
const FaqsSection = ({ label, required, faqs, onChange, icon: Icon, error }) => {
  const addFaq = () => {
    onChange([...faqs, { topic: "", details: "" }]);
  };

  const removeFaq = (index) => {
    onChange(faqs.filter((_, i) => i !== index));
  };

  const updateFaq = (index, field, value) => {
    const updated = faqs.map((f, i) =>
      i === index ? { ...f, [field]: value } : f
    );
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 text-[#F3F4F6] text-sm font-body">
        {Icon && <Icon className="w-4 h-4 text-[#00D1FF]" />}
        {label}
        {required && <span className="text-[#00D1FF]">*</span>}
      </label>

      <div className="space-y-3">
        {faqs.map((f, index) => (
          <FaqInput
            key={index}
            index={index}
            topic={f.topic}
            details={f.details}
            onTopicChange={(value) => updateFaq(index, "topic", value)}
            onDetailsChange={(value) => updateFaq(index, "details", value)}
            onRemove={() => removeFaq(index)}
            canRemove={faqs.length > 1 || !required}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={addFaq}
        className="flex items-center gap-2 text-[#00D1FF] text-sm font-body hover:text-[#00D1FF]/80 transition-colors py-2"
      >
        <Plus className="w-4 h-4" />
        Add FAQ
      </button>

      {error && <p className="text-red-400 text-xs font-body">{error}</p>}
    </div>
  );
};

// Step 1: Knowledge Base
const Step1KnowledgeBase = ({ formData, setFormData, errors }) => {
  const updateField = (field, value) => {
    setFormData({
      ...formData,
      knowledge_base: {
        ...formData.knowledge_base,
        [field]: value,
      },
    });
  };

  const updateHandoffField = (field, value) => {
    setFormData({
      ...formData,
      knowledge_base: {
        ...formData.knowledge_base,
        handoff_settings: {
          ...formData.knowledge_base.handoff_settings,
          [field]: value,
        },
      },
    });
  };

  return (
    <div className="space-y-6" data-testid="step-1">
      <div className="mb-8">
        <h2 className="font-heading font-bold text-2xl md:text-3xl text-[#F3F4F6] mb-2">
          Knowledge Base
        </h2>
        <p className="text-[#9CA3AF] font-body">
          Tell us about your property listings.
        </p>
      </div>

      <FormInput
        label="Agency Name"
        required
        type="text"
        placeholder="Your Agency Name"
        value={formData.knowledge_base.agency_name}
        onChange={(e) => updateField("agency_name", e.target.value)}
        error={errors.agency_name}
        icon={Building2}
        data-testid="agency-name-input"
      />

      <FormInput
        label="Agency Website URL"
        type="url"
        placeholder="https://your-agency.com.au"
        value={formData.knowledge_base.website_url}
        onChange={(e) => updateField("website_url", e.target.value)}
        error={errors.website_url}
        icon={Globe}
        data-testid="website-url-input"
      />

      <div className="pt-4 border-t border-white/10">
        <h3 className="text-[#F3F4F6] text-sm font-body font-semibold mb-4">Human Handoff Settings</h3>
        
        <div className="space-y-4">
          <FormInputWithTooltip
            label="Human Fallback Contact"
            sublabel="Who should the bot introduce when transferring a chat?"
            tooltip="When the AI encounters a question it is not allowed to answer (such as taxes, legal advice, or price negotiations), it will tell the client that this person will personally reach out to assist them."
            required
            type="text"
            placeholder="e.g., Alex"
            value={formData.knowledge_base.handoff_settings?.lead_agent_name || ""}
            onChange={(e) => updateHandoffField("lead_agent_name", e.target.value)}
            error={errors.lead_agent_name}
            icon={User}
            data-testid="lead-agent-name-input"
          />

          <FormInputWithTooltip
            label="Agent Position"
            sublabel="The job title of the fallback contact."
            tooltip="This adds a professional touch to AI responses. The bot will combine this role with the agent's name (e.g., 'I am escalating your offer to our Lead Sales Agent, Alex')."
            required
            type="text"
            placeholder="e.g., Senior Broker"
            value={formData.knowledge_base.handoff_settings?.lead_agent_role || ""}
            onChange={(e) => updateHandoffField("lead_agent_role", e.target.value)}
            error={errors.lead_agent_role}
            icon={Briefcase}
            data-testid="lead-agent-role-input"
          />

          <FormInputWithTooltip
            label="Agent Phone Number"
            sublabel="Phone number for the handoff agent."
            tooltip="The phone number that will be used when the AI hands off a conversation to a human agent. Leads may be contacted via this number."
            type="tel"
            placeholder="e.g., +61 400 000 000"
            value={formData.knowledge_base.handoff_settings?.lead_agent_phone || ""}
            onChange={(e) => updateHandoffField("lead_agent_phone", e.target.value)}
            error={errors.lead_agent_phone}
            icon={Phone}
            data-testid="lead-agent-phone-input"
          />

          <FormInputWithTooltip
            label="Agent Email"
            sublabel="Email address for the handoff agent."
            tooltip="The email address that will be used when the AI hands off a conversation to a human agent. Notifications and lead details may be sent here."
            type="email"
            placeholder="e.g., agent@agency.com.au"
            value={formData.knowledge_base.handoff_settings?.lead_agent_email || ""}
            onChange={(e) => updateHandoffField("lead_agent_email", e.target.value)}
            error={errors.lead_agent_email}
            icon={Mail}
            data-testid="lead-agent-email-input"
          />

          {/* Allowed Channels Multi-Select */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-[#D1D5DB] font-body">
                Allowed Channels
              </label>
              <div className="group relative">
                <Info className="w-3.5 h-3.5 text-[#6B7280] cursor-help" />
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-[#1F2937] text-[#D1D5DB] text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none w-64 z-50 border border-white/10 shadow-xl">
                  Select which channels the AI is allowed to use when handing off to the human agent.
                </div>
              </div>
            </div>
            <p className="text-xs text-[#6B7280] font-body mb-2">
              Select channels available for human handoff.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: "phone_call", label: "Phone Call", icon: Phone },
                { value: "sms", label: "SMS", icon: MessageSquare },
                { value: "whatsapp", label: "WhatsApp", icon: MessageCircle },
                { value: "telegram", label: "Telegram", icon: Smartphone },
              ].map((channel) => {
                const isSelected = (formData.knowledge_base.handoff_settings?.allowed_channels || []).includes(channel.value);
                const ChannelIcon = channel.icon;
                return (
                  <button
                    key={channel.value}
                    type="button"
                    onClick={() => {
                      const currentChannels = formData.knowledge_base.handoff_settings?.allowed_channels || [];
                      const newChannels = isSelected
                        ? currentChannels.filter((c) => c !== channel.value)
                        : [...currentChannels, channel.value];
                      updateHandoffField("allowed_channels", newChannels);
                    }}
                    className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border transition-all duration-200 text-sm font-body ${
                      isSelected
                        ? "border-[#D4AF37]/60 bg-[#D4AF37]/10 text-[#D4AF37]"
                        : "border-white/10 bg-white/[0.03] text-[#9CA3AF] hover:border-white/20 hover:bg-white/[0.05]"
                    }`}
                    data-testid={`channel-${channel.value}`}
                  >
                    <ChannelIcon className={`w-4 h-4 ${isSelected ? "text-[#D4AF37]" : "text-[#6B7280]"}`} />
                    <span>{channel.label}</span>
                    {isSelected && <Check className="w-3.5 h-3.5 ml-auto text-[#D4AF37]" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Step 2: Qualification Logic
const Step2QualificationLogic = ({ formData, setFormData, errors }) => {
  const updateBuyerQuestions = (questions) => {
    setFormData({
      ...formData,
      qualification_logic: {
        ...formData.qualification_logic,
        buyer_questions: questions,
      },
    });
  };

  // Vendor Questions - Hidden but kept for future use
  const updateVendorQuestions = (questions) => {
    setFormData({
      ...formData,
      qualification_logic: {
        ...formData.qualification_logic,
        vendor_questions: questions,
      },
    });
  };

  const updateTenantQuestions = (questions) => {
    setFormData({
      ...formData,
      qualification_logic: {
        ...formData.qualification_logic,
        tenant_questions: questions,
      },
    });
  };

  const updateUnqualifiedAction = (value) => {
    setFormData({
      ...formData,
      qualification_logic: {
        ...formData.qualification_logic,
        unqualified_action: value,
      },
    });
  };

  const updateStrictQualificationGate = (value) => {
    setFormData({
      ...formData,
      qualification_logic: {
        ...formData.qualification_logic,
        strict_qualification_gate: value === "yes",
      },
    });
  };

  return (
    <div className="space-y-6" data-testid="step-2">
      <div className="mb-8">
        <h2 className="font-heading font-bold text-2xl md:text-3xl text-[#F3F4F6] mb-2">
          Qualification Logic
        </h2>
        <p className="text-[#9CA3AF] font-body">
          Configure how the AI qualifies your leads.
        </p>
      </div>

      <QuestionsSection
        label="Buyer Qualification Questions"
        required
        questions={formData.qualification_logic.buyer_questions}
        onChange={updateBuyerQuestions}
        icon={HelpCircle}
        error={errors.buyer_questions}
      />

      {/* Vendor Questions - Hidden per requirements */}
      <QuestionsSection
        label="Vendor Questions"
        questions={formData.qualification_logic.vendor_questions}
        onChange={updateVendorQuestions}
        icon={Users}
      />

      <QuestionsSection
        label="Tenant Qualification Questions"
        questions={formData.qualification_logic.tenant_questions}
        onChange={updateTenantQuestions}
        icon={Users}
        error={errors.tenant_questions}
      />

      <RadioGroup
        label="Strict Qualification"
        required
        options={[
          { 
            value: "yes", 
            label: "Yes",
            description: "If the client asks to schedule a meeting but has not yet been qualified, politely ask follow-up qualifying questions and do not allow booking a viewing."
          },
          { 
            value: "no", 
            label: "No",
            description: "If the client asks to schedule a meeting but has not yet been qualified, allow available time slots to be offered. The broker will receive a note: \"Meeting scheduled, but the lead is not qualified.\""
          },
        ]}
        value={formData.qualification_logic.strict_qualification_gate ? "yes" : "no"}
        onChange={updateStrictQualificationGate}
        error={errors.strict_qualification_gate}
        icon={ShieldCheck}
      />

      <RadioGroup
        label="What to do with unqualified leads?"
        required
        options={[
          { value: "decline", label: "Politely decline" },
          { value: "website", label: "Send a link to the general website" },
          { value: "manager", label: "Send a message that an available manager will contact them shortly" },
        ]}
        value={formData.qualification_logic.unqualified_action}
        onChange={updateUnqualifiedAction}
        error={errors.unqualified_action}
        icon={MessageSquare}
      />
    </div>
  );
};

// Step 3: Seller FAQ Responses
const Step3SellerFaqs = ({ formData, setFormData, errors }) => {
  const updateVendorFaqs = (faqs) => {
    setFormData({
      ...formData,
      knowledge_base: {
        ...formData.knowledge_base,
        vendor_faqs: faqs,
      },
    });
  };

  return (
    <div className="space-y-6" data-testid="step-3-seller-faqs">
      <div className="mb-8">
        <h2 className="font-heading font-bold text-2xl md:text-3xl text-[#F3F4F6] mb-2">
          Seller FAQ Responses
        </h2>
        <p className="text-[#9CA3AF] font-body">
          Configure the information the bot will use to answer clients&apos; frequently asked questions. Describe your sales methods, home preparation recommendations, and your experience. You can edit the provided templates or add your own custom questions and answers.
        </p>
      </div>

      <FaqsSection
        label="Seller FAQs"
        faqs={formData.knowledge_base.vendor_faqs || []}
        onChange={updateVendorFaqs}
        icon={BookOpen}
        error={errors.vendor_faqs}
      />
    </div>
  );
};

// Step 4: Scheduling & Handoff
const Step3SchedulingHandoff = ({ formData, setFormData, errors }) => {
  const updateField = (field, value) => {
    setFormData({
      ...formData,
      scheduling: {
        ...formData.scheduling,
        [field]: value,
      },
    });
  };

  return (
    <div className="space-y-6" data-testid="step-4">
      <div className="mb-8">
        <h2 className="font-heading font-bold text-2xl md:text-3xl text-[#F3F4F6] mb-2">
          Scheduling & Handoff
        </h2>
        <p className="text-[#9CA3AF] font-body">
          Set up calendar integration and notification preferences.
        </p>
      </div>

      <FormInput
        label="Working Hours for Private Inspections"
        type="text"
        placeholder="e.g., Mon–Fri 10am–4pm"
        value={formData.scheduling.inspection_hours}
        onChange={(e) => updateField("inspection_hours", e.target.value)}
        icon={Clock}
        data-testid="working-hours-input"
      />
    </div>
  );
};

// Step 5: Tone of Voice
const Step4ToneOfVoice = ({ formData, setFormData, errors }) => {
  const updateField = (field, value) => {
    setFormData({
      ...formData,
      tone_of_voice: {
        ...formData.tone_of_voice,
        [field]: value,
      },
    });
  };

  return (
    <div className="space-y-6" data-testid="step-5">
      <div className="mb-8">
        <h2 className="font-heading font-bold text-2xl md:text-3xl text-[#F3F4F6] mb-2">
          Tone of Voice & Persona
        </h2>
        <p className="text-[#9CA3AF] font-body">
          Customize your AI assistant's personality.
        </p>
      </div>

      <FormInput
        label="AI Assistant Name"
        type="text"
        placeholder="e.g., Sarah, FractoBot, Emma"
        value={formData.tone_of_voice.bot_name}
        onChange={(e) => updateField("bot_name", e.target.value)}
        icon={User}
        data-testid="bot-name-input"
      />

      <RadioGroup
        label="Communication Style"
        required
        options={[
          { value: "professional_corporate", label: "Professional & Corporate" },
          { value: "friendly_energetic", label: "Friendly & Energetic" },
          { value: "premium_exclusive", label: "Premium & Exclusive" },
        ]}
        value={formData.tone_of_voice.style}
        onChange={(value) => updateField("style", value)}
        error={errors.style}
        icon={MessageCircle}
      />

      <div className="space-y-3">
        <ToggleSwitch
          label="Use emojis in messages"
          checked={formData.tone_of_voice.use_emojis}
          onChange={(checked) => updateField("use_emojis", checked)}
          icon={Smile}
        />
        <ToggleSwitch
          label='Use Australian slang (e.g., "mate", "cheers")'
          checked={formData.tone_of_voice.use_aussie_slang}
          onChange={(checked) => updateField("use_aussie_slang", checked)}
          icon={MessageSquare}
        />
      </div>
    </div>
  );
};

// Main BotSettings Component
const RealEstateBotSettings = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasExistingSettings, setHasExistingSettings] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const totalSteps = 5;

  const steps = [
    { shortTitle: "Knowledge Base" },
    { shortTitle: "Qualification" },
    { shortTitle: "Seller FAQs" },
    { shortTitle: "Scheduling" },
    { shortTitle: "Persona" },
  ];

  // Check if user has existing bot settings on mount
  useEffect(() => {
    const checkExistingSettings = async () => {
      const token = getClientAuthToken();
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const response = await axios.get(`${backendUrl}/api/bot/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.has_settings) {
          setHasExistingSettings(true);
          // Pre-populate form data with existing settings for edit mode
          const existingSettings = response.data.settings;
          const existingVendorFaqs = existingSettings.knowledge_base?.vendor_faqs;
          setFormData({
            knowledge_base: {
              ...initialFormData.knowledge_base,
              ...existingSettings.knowledge_base,
              handoff_settings: {
                ...initialFormData.knowledge_base.handoff_settings,
                ...(existingSettings.knowledge_base?.handoff_settings || {}),
              },
              vendor_faqs: Array.isArray(existingVendorFaqs) && existingVendorFaqs.length > 0
                ? existingVendorFaqs
                : initialFormData.knowledge_base.vendor_faqs,
            },
            qualification_logic: {
              ...initialFormData.qualification_logic,
              ...existingSettings.qualification_logic,
              buyer_questions: existingSettings.qualification_logic?.buyer_questions || initialFormData.qualification_logic.buyer_questions,
              vendor_questions: existingSettings.qualification_logic?.vendor_questions || initialFormData.qualification_logic.vendor_questions,
              tenant_questions: existingSettings.qualification_logic?.tenant_questions || initialFormData.qualification_logic.tenant_questions,
            },
            scheduling: existingSettings.scheduling || initialFormData.scheduling,
            tone_of_voice: existingSettings.tone_of_voice || initialFormData.tone_of_voice,
          });
        }
      } catch (error) {
        console.error("Error checking bot settings:", error);
        // If error, assume no settings and show form
      } finally {
        setIsLoading(false);
      }
    };

    checkExistingSettings();
  }, []);

  // Load saved data from localStorage on mount (only if not loaded from DB)
  useEffect(() => {
    if (!hasExistingSettings && !isLoading) {
      const savedData = localStorage.getItem(STORAGE_KEY);
      if (savedData) {
        try {
          const parsed = JSON.parse(savedData);
          // Deep merge with initial form data to handle missing fields
          const parsedVendorFaqs = parsed.knowledge_base?.vendor_faqs;
          setFormData({
            knowledge_base: { 
              ...initialFormData.knowledge_base, 
              ...parsed.knowledge_base,
              handoff_settings: {
                ...initialFormData.knowledge_base.handoff_settings,
                ...(parsed.knowledge_base?.handoff_settings || {}),
              },
              vendor_faqs: Array.isArray(parsedVendorFaqs) && parsedVendorFaqs.length > 0
                ? parsedVendorFaqs
                : initialFormData.knowledge_base.vendor_faqs,
            },
            qualification_logic: { ...initialFormData.qualification_logic, ...parsed.qualification_logic },
            scheduling: { ...initialFormData.scheduling, ...parsed.scheduling },
            tone_of_voice: { ...initialFormData.tone_of_voice, ...parsed.tone_of_voice },
          });
        } catch (e) {
          console.error("Error loading saved data:", e);
        }
      }
    }
  }, [hasExistingSettings, isLoading]);

  // Auto-save to localStorage whenever formData changes (only for new forms, not edit mode)
  const saveToLocalStorage = useCallback(() => {
    if (!hasExistingSettings || isEditMode) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    }
  }, [formData, hasExistingSettings, isEditMode]);

  useEffect(() => {
    saveToLocalStorage();
  }, [saveToLocalStorage]);

  // Validation for each step
  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!formData.knowledge_base.agency_name || !formData.knowledge_base.agency_name.trim()) {
          newErrors.agency_name = "Agency name is required";
        }
        if (formData.knowledge_base.website_url && !validateUrl(formData.knowledge_base.website_url)) {
          newErrors.website_url = "Please enter a valid URL";
        }
        if (!formData.knowledge_base.handoff_settings?.lead_agent_name || !formData.knowledge_base.handoff_settings.lead_agent_name.trim()) {
          newErrors.lead_agent_name = "Human Fallback Contact is required";
        }
        if (!formData.knowledge_base.handoff_settings?.lead_agent_role || !formData.knowledge_base.handoff_settings.lead_agent_role.trim()) {
          newErrors.lead_agent_role = "Agent Position is required";
        }
        break;

      case 2:
        if (formData.qualification_logic.buyer_questions.length === 0) {
          newErrors.buyer_questions = "At least one buyer question is required";
        } else if (formData.qualification_logic.buyer_questions.some(q => !q.question.trim())) {
          newErrors.buyer_questions = "All questions must be filled";
        }
        if (!formData.qualification_logic.unqualified_action) {
          newErrors.unqualified_action = "Please select an option";
        }
        break;

      case 3:
        // Seller FAQs: optional, but if any entry exists with a topic it should have details, and vice versa
        if (Array.isArray(formData.knowledge_base.vendor_faqs)) {
          const hasInvalid = formData.knowledge_base.vendor_faqs.some(
            (f) => (f.topic && !f.topic.trim() && f.details && f.details.trim()) ||
                   (f.topic && f.topic.trim() && !(f.details && f.details.trim()))
          );
          if (hasInvalid) {
            newErrors.vendor_faqs = "Each FAQ must have both a topic and details, or be removed.";
          }
        }
        break;

      case 4:
        break;

      case 5:
        if (!formData.tone_of_voice.style) {
          newErrors.style = "Please select a communication style";
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setErrors({});
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSubmit = async () => {
    if (validateStep(currentStep)) {
      setIsSubmitting(true);
      
      // Prepare final data for submission
      const submissionData = {
        knowledge_base: {
          agency_name: formData.knowledge_base.agency_name,
          website_url: formData.knowledge_base.website_url,
          handoff_settings: {
            lead_agent_name: formData.knowledge_base.handoff_settings?.lead_agent_name || "",
            lead_agent_role: formData.knowledge_base.handoff_settings?.lead_agent_role || "",
            lead_agent_phone: formData.knowledge_base.handoff_settings?.lead_agent_phone || "",
            lead_agent_email: formData.knowledge_base.handoff_settings?.lead_agent_email || "",
            allowed_channels: formData.knowledge_base.handoff_settings?.allowed_channels || [],
          },
          vendor_faqs: (formData.knowledge_base.vendor_faqs || [])
            .filter((f) => (f.topic && f.topic.trim()) || (f.details && f.details.trim()))
            .map((f) => ({
              topic: (f.topic || "").trim(),
              details: (f.details || "").trim(),
            })),
        },
        qualification_logic: {
          buyer_questions: formData.qualification_logic.buyer_questions,
          vendor_questions: formData.qualification_logic.vendor_questions,
          tenant_questions: formData.qualification_logic.tenant_questions,
          unqualified_action: formData.qualification_logic.unqualified_action,
          strict_qualification_gate: formData.qualification_logic.strict_qualification_gate,
        },
        scheduling: {
          inspection_hours: formData.scheduling.inspection_hours,
        },
        tone_of_voice: {
          bot_name: formData.tone_of_voice.bot_name,
          style: formData.tone_of_voice.style,
          use_emojis: formData.tone_of_voice.use_emojis,
          use_aussie_slang: formData.tone_of_voice.use_aussie_slang,
        },
      };

      try {
        const token = getClientAuthToken();
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        
        const headers = {
          "Content-Type": "application/json",
        };
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }
        
        const response = await axios.post(`${backendUrl}/api/bot/save_settings`, submissionData, { headers });
        
        if (response.data && response.data.status === "ok") {
          console.log("Form submitted successfully:", response.data);
          localStorage.removeItem(STORAGE_KEY);
          setIsSubmitting(false);
          setIsSubmitted(true);
          setHasExistingSettings(true);
          setIsEditMode(false);
        } else {
          console.error("Submission error:", response.data);
          setErrors({ submit: response.data?.detail || response.data?.message || "Failed to save settings. Please try again." });
          setIsSubmitting(false);
        }
      } catch (error) {
        console.error("Network error:", error);
        const errorMessage = error.response?.data?.detail || error.response?.data?.message || "Network error. Please check your connection and try again.";
        setErrors({ submit: errorMessage });
        setIsSubmitting(false);
      }
    }
  };

  // Handle Edit Settings button click
  const handleEditSettings = () => {
    setIsEditMode(true);
    setIsSubmitted(false);
    setCurrentStep(1);
  };

  // Handle Add Listings button click
  const handleAddListings = () => {
    navigate("/dashboard/listings");
  };

  const isNextDisabled = () => {
    switch (currentStep) {
      case 1:
        return !formData.knowledge_base.agency_name || !formData.knowledge_base.agency_name.trim() ||
               !formData.knowledge_base.handoff_settings?.lead_agent_name || !formData.knowledge_base.handoff_settings.lead_agent_name.trim() ||
               !formData.knowledge_base.handoff_settings?.lead_agent_role || !formData.knowledge_base.handoff_settings.lead_agent_role.trim();
      case 2:
        return formData.qualification_logic.buyer_questions.length === 0 || 
               formData.qualification_logic.buyer_questions.some(q => !q.question.trim()) ||
               !formData.qualification_logic.unqualified_action;
      case 3:
        return false;
      case 4:
        return false;
      case 5:
        return !formData.tone_of_voice.style;
      default:
        return false;
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1KnowledgeBase formData={formData} setFormData={setFormData} errors={errors} />;
      case 2:
        return <Step2QualificationLogic formData={formData} setFormData={setFormData} errors={errors} />;
      case 3:
        return <Step3SellerFaqs formData={formData} setFormData={setFormData} errors={errors} />;
      case 4:
        return <Step3SchedulingHandoff formData={formData} setFormData={setFormData} errors={errors} />;
      case 5:
        return <Step4ToneOfVoice formData={formData} setFormData={setFormData} errors={errors} />;
      default:
        return null;
    }
  };

  // Loading State
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0F1115] flex">
        <Sidebar />
        <div className="flex-1 md:ml-64 flex items-center justify-center px-6">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-[#00D1FF] animate-spin mx-auto mb-4" />
            <p className="text-[#9CA3AF] font-body">Loading your settings...</p>
          </div>
        </div>
      </div>
    );
  }

  // Settings Configured State (show when user has settings and not in edit mode)
  if ((hasExistingSettings && !isEditMode) || isSubmitted) {
    return (
      <div className="min-h-screen bg-[#0F1115] flex">
        {/* Sidebar */}
        <Sidebar />
        
        {/* Main Content */}
        <div className="flex-1 md:ml-64 flex items-center justify-center px-6 py-12">
          <div className="max-w-lg w-full text-center">
            {/* Success Icon */}
            <div className="w-24 h-24 rounded-full bg-[#00D1FF]/20 flex items-center justify-center mx-auto mb-8 animate-pulse">
              <Sparkles className="w-12 h-12 text-[#00D1FF]" />
            </div>
            
            {/* Main Message */}
            <h1 className="font-heading font-bold text-3xl md:text-4xl text-[#F3F4F6] mb-4">
              Settings Received!
            </h1>
            <p className="text-[#9CA3AF] font-body text-lg mb-6">
              Your settings have been received and the bot will soon be trained on your data.
            </p>

            {/* Listings CTA Card */}
            <div className="glass rounded-2xl p-6 border border-[#00D1FF]/20 mb-8">
              <div className="flex items-center justify-center gap-3 mb-3">
                <Home className="w-6 h-6 text-[#00D1FF]" />
                <h2 className="font-heading font-semibold text-xl text-[#F3F4F6]">
                  Add Your Listings
                </h2>
              </div>
              <p className="text-[#9CA3AF] font-body">
                Add listings so the AI Broker can navigate the available properties.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleEditSettings}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg border border-white/20 text-[#F3F4F6] font-heading font-semibold hover:bg-white/5 transition-colors"
              >
                <Pencil className="w-5 h-5" />
                Edit Settings
              </button>
              <button
                onClick={handleAddListings}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#00D1FF] text-[#0F1115] font-heading font-bold hover:bg-[#00D1FF]/90 transition-colors btn-pulse"
              >
                <Plus className="w-5 h-5" />
                Add Listings
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F1115] flex" data-testid="bot-settings-page">
      {/* Sidebar */}
      <Sidebar />

      {/* Main Content Container with left margin for sidebar (desktop only) */}
      <div className="flex-1 md:ml-64">
        {/* Main Content */}
        <main className="pt-8 pb-32 px-6">
          <div className="max-w-2xl mx-auto">
            {/* Step Indicator */}
            <StepIndicator steps={steps} currentStep={currentStep} />

            {/* Progress Bar */}
            <div className="mb-10">
              <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />
            </div>

            {/* Form Card */}
            <div className="glass rounded-2xl p-6 md:p-10 border border-white/10">
              {renderStep()}
            </div>
          </div>
        </main>

        {/* Fixed Bottom Navigation */}
        <div className="fixed bottom-0 left-0 md:left-64 right-0 bg-[#0F1115]/95 backdrop-blur-lg border-t border-white/5 p-4 md:p-6">
          <div className="max-w-2xl mx-auto">
            {/* Submission Error Display */}
            {errors.submit && (
              <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm font-body text-center">
                {errors.submit}
              </div>
            )}
            
            <div className="flex items-center justify-between gap-4">
              {/* Back Button */}
              {currentStep > 1 ? (
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 px-6 py-3 rounded-lg border border-white/20 text-[#F3F4F6] font-heading font-semibold hover:bg-white/5 transition-colors"
                  data-testid="back-button"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Back
                </button>
              ) : (
                <div /> // Spacer
              )}

            {/* Next/Submit Button */}
            {currentStep < totalSteps ? (
              <button
                onClick={handleNext}
                disabled={isNextDisabled()}
                className={`flex items-center gap-2 px-8 py-3 rounded-lg font-heading font-bold transition-all ${
                  isNextDisabled()
                    ? "bg-[#00D1FF]/30 text-[#0F1115]/50 cursor-not-allowed"
                    : "btn-pulse bg-[#00D1FF] text-[#0F1115] hover:bg-[#00D1FF]/90"
                }`}
                data-testid="next-button"
              >
                Next
              <ArrowRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isNextDisabled() || isSubmitting}
              className={`flex items-center gap-2 px-8 py-3 rounded-lg font-heading font-bold transition-all ${
                isNextDisabled() || isSubmitting
                  ? "bg-[#00D1FF]/30 text-[#0F1115]/50 cursor-not-allowed"
                  : "btn-pulse bg-[#00D1FF] text-[#0F1115] hover:bg-[#00D1FF]/90"
              }`}
              data-testid="submit-button"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-[#0F1115]/30 border-t-[#0F1115] rounded-full animate-spin" />
                  Launching...
                </>
              ) : (
                <>
                  <Rocket className="w-5 h-5" />
                  Launch My AI Broker
                </>
              )}
            </button>
          )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

import { getEffectiveBotType } from "@/lib/authCookies";

// Wrapper component that dispatches to the correct bot settings screen based
// on bot_type (including partner impersonation).
const BotSettings = () => {
  const botType = getEffectiveBotType();

  if (botType === "roofing") {
    return <RoofingBotSettings />;
  }

  // Default: real_estate (or no bot_type cookie)
  return <RealEstateBotSettings />;
};

export default BotSettings;
